"use client";

import Link from "next/link";
import { usePathname } from "next/navigation";
import { motion } from "framer-motion";
import dynamic from "next/dynamic";
import StarBorder from "@/components/react-bits/StarBorder";
import { useRef, useSyncExternalStore } from "react";
import gsap from "gsap";
import { localeFromPath } from "@/config/i18n";
import { tr, type I18n } from "@/config/localeText";

import hero from "@/assets/images/insta/POST_IG_15.jpg";

// Effet WebGL (three.js) chargé uniquement côté client, après le montage, pour
// ne pas alourdir le bundle de la page 404. Le visuel de fond (image + dégradés)
// reste rendu derrière, donc la page n'est jamais vide.
const GridDistortion = dynamic(
  () => import("@/components/react-bits/GridDistortion"),
  { ssr: false },
);

type NotFoundCopy = {
  eyebrow: string;
  lead: string;
  sub: string;
  chauffeurEyebrow: string;
  chauffeurTitle: string;
  chauffeurText: string;
  chauffeurCta: string;
  rentalEyebrow: string;
  rentalTitle: string;
  rentalText: string;
  rentalCta: string;
  exploreChauffeur: string;
  exploreRental: string;
  contact: string;
};

const NOT_FOUND_UI: I18n<NotFoundCopy> = {
  fr: {
    eyebrow: "Redirection intelligente",
    lead: "Cette page n'existe pas, mais votre besoin est probablement l'un des deux parcours cles ci-dessous.",
    sub: "Choisissez votre destination et accedez directement au tunnel de conversion adapte.",
    chauffeurEyebrow: "Chauffeur prive",
    chauffeurTitle: "Reserver un trajet premium",
    chauffeurText:
      "Transferts, mise a disposition, evenements et besoins corporate sur la Cote d'Azur.",
    chauffeurCta: "Reserver un chauffeur",
    rentalEyebrow: "Location",
    rentalTitle: "Louer un vehicule d'exception",
    rentalText:
      "Consultez la flotte complete et accedez aux fiches vehicules pour reserver rapidement.",
    rentalCta: "Voir les vehicules",
    exploreChauffeur: "Explorer Chauffeur",
    exploreRental: "Explorer Location",
    contact: "Nous contacter",
  },
  en: {
    eyebrow: "Smart redirect",
    lead: "This page doesn't exist, but what you need is probably one of the two key journeys below.",
    sub: "Choose your destination and go straight to the right conversion funnel.",
    chauffeurEyebrow: "Private chauffeur",
    chauffeurTitle: "Book a premium ride",
    chauffeurText:
      "Transfers, hourly hire, events and corporate needs across the French Riviera.",
    chauffeurCta: "Book a chauffeur",
    rentalEyebrow: "Rental",
    rentalTitle: "Rent an exceptional vehicle",
    rentalText:
      "Browse the full fleet and open the vehicle pages to book quickly.",
    rentalCta: "View the vehicles",
    exploreChauffeur: "Explore Chauffeur",
    exploreRental: "Explore Rental",
    contact: "Contact us",
  },
  ar: {
    eyebrow: "إعادة توجيه ذكية",
    lead: "هذه الصفحة غير موجودة، لكنّ ما تحتاجه غالبًا أحد المسارين الرئيسيين أدناه.",
    sub: "اختر وجهتك وانتقل مباشرةً إلى المسار المناسب لإتمام طلبك.",
    chauffeurEyebrow: "سائق خاص",
    chauffeurTitle: "احجز رحلة راقية",
    chauffeurText:
      "نقل، وخدمة بالساعة، وفعاليات، واحتياجات الشركات على امتداد الريفييرا الفرنسية.",
    chauffeurCta: "احجز سائقًا",
    rentalEyebrow: "التأجير",
    rentalTitle: "استأجر سيارة استثنائية",
    rentalText: "اطّلع على الأسطول الكامل وافتح صفحات السيارات للحجز بسرعة.",
    rentalCta: "عرض السيارات",
    exploreChauffeur: "استكشف خدمة السائق",
    exploreRental: "استكشف التأجير",
    contact: "تواصل معنا",
  },
};

const Digits404 = (): JSX.Element => {
  const wrapperRef = useRef<HTMLDivElement | null>(null);

  const handleEnter = (): void => {
    if (!wrapperRef.current) return;
    const digits = wrapperRef.current.querySelectorAll(".digit");
    gsap.to(wrapperRef.current, {
      "--grad-angle": "315deg",
      duration: 0.8,
      ease: "power3.out",
    });
    gsap.to(digits, {
      scale: 1.06,
      y: -6,
      duration: 0.6,
      ease: "power3.out",
      stagger: 0.06,
    });
  };

  const handleLeave = (): void => {
    if (!wrapperRef.current) return;
    const digits = wrapperRef.current.querySelectorAll(".digit");
    gsap.to(wrapperRef.current, {
      "--grad-angle": "135deg",
      duration: 0.8,
      ease: "power3.inOut",
    });
    gsap.to(digits, {
      scale: 1,
      y: 0,
      duration: 0.5,
      ease: "power3.inOut",
      stagger: 0.04,
    });
  };

  return (
    <div
      ref={wrapperRef}
      className="relative mb-6 flex items-end justify-center gap-2 sm:gap-3"
      onMouseEnter={handleEnter}
      onMouseLeave={handleLeave}
    >
      {(["4", "0", "4"] as const).map((digit, i) => (
        <motion.span
          key={digit + i}
          initial={false}
          animate={{ y: 0, opacity: 1, rotate: 0 }}
          transition={{
            duration: 0.7,
            delay: 0.08 * i,
            ease: [0.22, 1, 0.36, 1],
          }}
          className="digit font-space-age text-[clamp(4rem,26vw,12rem)] leading-none tracking-[0.08em] text-transparent sm:tracking-[0.1em] md:tracking-[0.12em]"
          style={{
            backgroundImage:
              "linear-gradient(var(--grad-angle,135deg), #D4AF37, #f5d57a)",
            WebkitBackgroundClip: "text",
          }}
        >
          {digit}
        </motion.span>
      ))}
      <span className="pointer-events-none absolute inset-x-0 -bottom-4 mx-auto h-[90px] w-[240px] rounded-full blur-3xl [background:radial-gradient(140px_50px_at_50%_50%,rgba(212,175,55,0.35),transparent_60%)] sm:h-[120px] sm:w-[360px]" />
    </div>
  );
};

const NotFound = (): JSX.Element => {
  const pathname = usePathname();
  const locale = localeFromPath(pathname ?? "/");
  const t = tr(NOT_FOUND_UI, locale);
  // Gate de montage SSG-safe sans setState-in-effect (cf. règle
  // react-hooks/set-state-in-effect) : false au SSR + 1er rendu, true ensuite.
  const mounted = useSyncExternalStore(
    () => () => {},
    () => true,
    () => false,
  );

  return (
    <main className="min-h-screen bg-black text-white">
      <section className="relative isolate grid min-h-[100vh] w-full overflow-hidden">
        <div className="pointer-events-none sticky top-0 -z-10 h-[100vh] w-full [grid-area:1/1]">
          {/* Poster statique : visible immédiatement et tant que l'effet WebGL
              n'est pas monté, pour éviter un fond noir et préserver le LCP. */}
          <div
            className="absolute inset-0 bg-cover bg-center"
            style={{ backgroundImage: `url(${hero.src})` }}
            aria-hidden="true"
          />
          {mounted && (
            <GridDistortion
              imageSrc={hero.src}
              grid={15}
              mouse={0.1}
              strength={0.15}
              relaxation={0.9}
              className="h-full w-full"
            />
          )}
          <div className="absolute inset-0 bg-black/45" />
          <div className="absolute inset-0 [background:radial-gradient(circle_at_center,rgba(0,0,0,0.38)_0%,rgba(0,0,0,0.58)_52%,rgba(0,0,0,0.8)_100%)]" />
          <div className="absolute inset-0 [background:linear-gradient(to_bottom,rgba(0,0,0,0.62)_0%,rgba(0,0,0,0.42)_38%,rgba(0,0,0,0.68)_100%)]" />
        </div>

        <div className="relative z-10 mx-auto flex w-full max-w-[100rem] flex-col items-center justify-center px-4 py-16 text-center [grid-area:1/1] sm:px-6 md:px-8 md:py-24">
          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            className="mb-5 rounded-full border border-white/15 px-3 py-1 text-[11px] uppercase tracking-[0.25em] text-white/70 sm:text-xs sm:tracking-[0.35em]"
          >
            {t.eyebrow}
          </motion.div>

          <Digits404 />

          <motion.p
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55 }}
            className="mb-3 max-w-3xl text-base text-white/90 sm:text-lg"
          >
            {t.lead}
          </motion.p>

          <motion.p
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.55, delay: 0.06 }}
            className="mb-8 max-w-2xl text-sm text-white/70 sm:text-base"
          >
            {t.sub}
          </motion.p>

          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mb-6 grid w-full max-w-4xl gap-4 md:grid-cols-2"
          >
            <StarBorder
              as="div"
              color="#D4AF37"
              thickness={2}
              speed="9s"
              className="w-full"
            >
              <div className="bg-black/72 rounded-xl p-5 text-start backdrop-blur-md">
                <p className="mb-2 text-xs uppercase tracking-[0.22em] text-white/65">
                  {t.chauffeurEyebrow}
                </p>
                <h2 className="mb-2 text-lg font-semibold">
                  {t.chauffeurTitle}
                </h2>
                <p className="mb-4 text-sm text-white/75">{t.chauffeurText}</p>
                <Link
                  href="/reservation"
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-gold-600 hover:text-white md:px-6"
                >
                  {t.chauffeurCta}
                </Link>
              </div>
            </StarBorder>

            <StarBorder
              as="div"
              color="#D4AF37"
              thickness={2}
              speed="10s"
              className="w-full"
            >
              <div className="bg-black/72 rounded-xl p-5 text-start backdrop-blur-md">
                <p className="mb-2 text-xs uppercase tracking-[0.22em] text-white/65">
                  {t.rentalEyebrow}
                </p>
                <h2 className="mb-2 text-lg font-semibold">{t.rentalTitle}</h2>
                <p className="mb-4 text-sm text-white/75">{t.rentalText}</p>
                <Link
                  href="/location/vehicules"
                  className="inline-flex items-center gap-2 rounded-lg bg-primary px-5 py-2.5 text-sm font-semibold text-black transition-colors hover:bg-gold-600 hover:text-white md:px-6"
                >
                  {t.rentalCta}
                </Link>
              </div>
            </StarBorder>
          </motion.div>

          <motion.div
            initial={false}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.45, delay: 0.08 }}
            className="grid w-full max-w-3xl gap-3 sm:grid-cols-3"
          >
            <Link
              href="/chauffeur"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-white/20 px-5 py-2.5 text-sm font-semibold text-white/90 transition-colors hover:border-primary md:px-6"
            >
              {t.exploreChauffeur}
            </Link>
            <Link
              href="/location"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-white/20 px-5 py-2.5 text-sm font-semibold text-white/90 transition-colors hover:border-primary md:px-6"
            >
              {t.exploreRental}
            </Link>
            <Link
              href="/contact"
              className="inline-flex min-h-11 items-center justify-center gap-2 rounded-lg border border-white/20 px-5 py-2.5 text-sm font-semibold text-white/90 transition-colors hover:border-primary md:px-6"
            >
              {t.contact}
            </Link>
          </motion.div>
        </div>
      </section>
    </main>
  );
};

export default NotFound;
